import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Button, Typography, CircularProgress, Alert, Grid, Card, CardContent, CardActions,
    CardMedia, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Paper
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { getInventoryItems, deleteInventoryItem } from '../services/inventoryService';
import { toast } from 'react-toastify';
import InventoryForm from '../components/Inventory/InventoryForm';
import { useLanguage } from '../context/LanguageContext';
import PageContainer from '../components/PageContainer';
import ContentSection from '../components/ContentSection';

const Inventory = () => {
    const { t } = useLanguage();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [formOpen, setFormOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    const fetchItems = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getInventoryItems();
            setItems(response.data);
        } catch (err) {
            console.error("Error fetching inventory items:", err);
            setError(t('inventory.fetchError'));
            toast.error(t('inventory.fetchErrorToast'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleFormOpen = (item = null) => {
        setSelectedItem(item);
        setFormOpen(true);
    };

    const handleFormClose = () => {
        setFormOpen(false);
        setSelectedItem(null);
        fetchItems();
    };

    const handleDeleteClick = (item) => {
        setItemToDelete(item);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!itemToDelete) return;
        try {
            await deleteInventoryItem(itemToDelete._id);
            toast.success(t('inventory.deleteSuccessToast'));
            setItems(prevItems => prevItems.filter(item => item._id !== itemToDelete._id));
        } catch (err) {
            console.error("Error deleting inventory item:", err);
            toast.error(t('inventory.deleteErrorToast'));
        } finally {
            setDeleteDialogOpen(false);
            setItemToDelete(null);
        }
    };

    const filteredItems = items.filter(item => {
        const q = searchTerm.toLowerCase();
        return (
            item.maker?.toLowerCase().includes(q) ||
            item.model?.toLowerCase().includes(q) ||
            item.type?.toLowerCase().includes(q) ||
            item.quantity?.toString().includes(q) ||
            item.notes?.toLowerCase().includes(q)
        );
    });

    return (
        <PageContainer title={t('inventory.title')}>
            <ContentSection
                title={t('inventory.listTitle')}
                actions={
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => handleFormOpen()}
                    >
                        {t('inventory.addItem')}
                    </Button>
                }
            >
                <Paper sx={{ p: 2, mb: 3 }}>
                    <TextField
                        fullWidth
                        label={t('inventory.searchPlaceholder')}
                        variant="outlined"
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                </Paper>

                {loading && <CircularProgress />}
                {error && <Alert severity="error">{error}</Alert>}

                {!loading && !error && (
                    <Grid container spacing={3}>
                        {filteredItems.map(item => (
                            <Grid item xs={12} sm={6} md={4} key={item._id}>
                                <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                    <CardMedia
                                        component="img"
                                        height="140"
                                        image={item.imagePath ? `${process.env.REACT_APP_API_BASE_URL || ''}${item.imagePath}` : 'https://via.placeholder.com/150'}
                                        alt={item.model}
                                    />
                                    <CardContent sx={{ flexGrow: 1 }}>
                                        <Typography gutterBottom variant="h6" component="div">
                                            {item.maker} {item.model}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {t('inventory.type')}: {item.type}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {t('inventory.quantity')}: {item.quantity}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" noWrap>
                                            {t('inventory.notes')}: {item.notes}
                                        </Typography>
                                    </CardContent>
                                    <CardActions>
                                        <Tooltip title={t('common.edit')}>
                                            <IconButton onClick={() => handleFormOpen(item)}>
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('common.delete')}>
                                            <IconButton onClick={() => handleDeleteClick(item)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}

                <Dialog open={formOpen} onClose={handleFormClose} maxWidth="md" fullWidth>
                    <DialogTitle>{selectedItem ? t('inventory.editTitle') : t('inventory.addTitle')}</DialogTitle>
                    <DialogContent>
                        <InventoryForm item={selectedItem} onSave={handleFormClose} />
                    </DialogContent>
                </Dialog>

                <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                    <DialogTitle>{t('inventory.deleteConfirmTitle')}</DialogTitle>
                    <DialogContent>
                        <Typography>{t('inventory.deleteConfirmText')}</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
                        <Button onClick={handleDeleteConfirm} color="error">{t('common.delete')}</Button>
                    </DialogActions>
                </Dialog>
            </ContentSection>
        </PageContainer>
    );
};

export default Inventory;
